import * as React from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  getMonth,
  getYear,
  setMonth as dfSetMonth,
  setYear as dfSetYear,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GlassDatePickerProps {
  value: string; // 'YYYY-MM-DD' ou ''
  onChange: (value: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
}

type View = 'days' | 'months' | 'years';

const WEEK_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr',
  'Mai', 'Jun', 'Jul', 'Ago',
  'Set', 'Out', 'Nov', 'Dez',
];

/** Retorna o primeiro ano do grupo de 12 que contém `year`. */
function calcYgStart(year: number): number {
  return Math.floor((year - 1) / 12) * 12 + 1;
}

function buildCalendarDays(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days: Date[] = [];
  let cur = start;
  while (cur <= end) {
    days.push(cur);
    cur = addDays(cur, 1);
  }
  return days;
}

function toIso(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function parseValue(v: string): Date | null {
  if (!v) return null;
  try {
    const d = parseISO(v);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

// Classes compartilhadas entre as células de meses e anos
const CELL_BASE =
  'flex items-center justify-center rounded-md text-xs font-medium transition cursor-pointer select-none h-9';
const CELL_DEFAULT =
  'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]';
const CELL_SELECTED = 'bg-[var(--accent)] text-white hover:opacity-90';
const CELL_TODAY = 'ring-1 ring-[var(--accent)] text-[var(--accent)]';

export function GlassDatePicker({
  value,
  onChange,
  placeholder = 'Selecione a data',
  min,
  max,
  disabled = false,
  className,
}: GlassDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<View>('days');
  const [month, setMonth] = React.useState<Date>(() => {
    const parsed = parseValue(value);
    return parsed ?? new Date();
  });
  const [ygStart, setYgStart] = React.useState<number>(() =>
    calcYgStart(getYear(parseValue(value) ?? new Date()))
  );
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  const selected = parseValue(value);

  // Sincroniza o mês exibido quando value muda externamente
  React.useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) setMonth(parsed);
  }, [value]);

  // Ao abrir: reseta pra view 'days' e sincroniza o grupo de anos
  React.useEffect(() => {
    if (open) {
      setView('days');
      const ref = parseValue(value) ?? new Date();
      setYgStart(calcYgStart(getYear(ref)));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fecha ao clicar fora ou pressionar Escape
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // --- ações ---

  function selectDay(day: Date) {
    const iso = toIso(day);
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange(iso);
    setView('days');
    setOpen(false);
  }

  function selectMonth(monthIndex: number) {
    setMonth((m) => dfSetMonth(m, monthIndex));
    setView('days');
  }

  function selectYear(year: number) {
    setMonth((m) => dfSetYear(m, year));
    setYgStart(calcYgStart(year));
    setView('months');
  }

  function handlePrev() {
    if (view === 'days') setMonth((m) => subMonths(m, 1));
    else if (view === 'months') setMonth((m) => subYears(m, 1));
    else setYgStart((s) => s - 12);
  }

  function handleNext() {
    if (view === 'days') setMonth((m) => addMonths(m, 1));
    else if (view === 'months') setMonth((m) => addYears(m, 1));
    else setYgStart((s) => s + 12);
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  }

  function goToday(e: React.MouseEvent) {
    e.stopPropagation();
    const todayDate = new Date();
    const iso = toIso(todayDate);
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange(iso);
    setMonth(todayDate);
    setYgStart(calcYgStart(getYear(todayDate)));
    setView('days');
    setOpen(false);
  }

  // --- dados derivados ---

  const days = buildCalendarDays(month);
  const currentYear = getYear(month);
  const currentMonthIdx = getMonth(month);
  const yearGroup = Array.from({ length: 12 }, (_, i) => ygStart + i);
  const displayValue = selected ? format(selected, 'dd/MM/yyyy') : '';
  const nowYear = getYear(new Date());
  const nowMonth = getMonth(new Date());

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'border-[var(--border)] text-[var(--fg)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'ring-2 ring-[var(--accent)]',
        )}
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
        <span className={cn('flex-1 text-left truncate', !displayValue && 'text-[var(--fg-muted)]')}>
          {displayValue || placeholder}
        </span>
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 mt-1 w-64 overflow-hidden rounded-xl border shadow-lg',
            'border-[var(--popover-border,var(--card-border))]',
            'backdrop-blur-[var(--blur,0px)]',
          )}
          style={{
            background: 'var(--popover-bg, var(--card-bg))',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {/* Header — setas + título clicável */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--popover-border,var(--card-border))]">
            <button
              type="button"
              onClick={handlePrev}
              className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5 text-[var(--fg)]" />
            </button>

            {/* Título: muda conforme view */}
            {view === 'days' && (
              <div className="flex items-center gap-0.5 text-xs font-semibold text-[var(--fg)]">
                <button
                  type="button"
                  onClick={() => setView('months')}
                  className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition capitalize"
                >
                  {format(month, 'MMMM', { locale: ptBR })}
                </button>
                <button
                  type="button"
                  onClick={() => setView('years')}
                  className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
                >
                  {currentYear}
                </button>
              </div>
            )}

            {view === 'months' && (
              <button
                type="button"
                onClick={() => setView('years')}
                className="text-xs font-semibold text-[var(--fg)] rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
              >
                {currentYear}
              </button>
            )}

            {view === 'years' && (
              <span className="text-xs font-semibold text-[var(--fg)] select-none">
                {ygStart} – {ygStart + 11}
              </span>
            )}

            <button
              type="button"
              onClick={handleNext}
              className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
              aria-label="Próximo"
            >
              <ChevronRight className="h-3.5 w-3.5 text-[var(--fg)]" />
            </button>
          </div>

          {/* View: dias */}
          {view === 'days' && (
            <>
              {/* Labels da semana */}
              <div className="grid grid-cols-7 px-2 pt-2">
                {WEEK_LABELS.map((l, i) => (
                  <div
                    key={i}
                    className="flex h-7 items-center justify-center text-[10px] font-medium text-[var(--fg-muted)]"
                  >
                    {l}
                  </div>
                ))}
              </div>

              {/* Grid de dias */}
              <div className="grid grid-cols-7 px-2 pb-2">
                {days.map((day, idx) => {
                  const iso = toIso(day);
                  const isCurrentMonth = isSameMonth(day, month);
                  const isSelectedDay = selected ? isSameDay(day, selected) : false;
                  const isTodayDay = isToday(day);
                  const isDisabledDay = (!!min && iso < min) || (!!max && iso > max);

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isDisabledDay}
                      onClick={() => selectDay(day)}
                      className={cn(
                        'flex h-7 w-full items-center justify-center rounded-md text-xs transition',
                        !isCurrentMonth && 'text-[var(--fg-muted)] opacity-40',
                        isCurrentMonth && !isSelectedDay && !isTodayDay &&
                          'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                        isTodayDay && !isSelectedDay &&
                          'ring-1 ring-[var(--accent)] text-[var(--accent)]',
                        isSelectedDay &&
                          'bg-[var(--accent)] text-white font-medium hover:opacity-90',
                        isDisabledDay && 'cursor-not-allowed opacity-30',
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* View: meses — grade 4×3 */}
          {view === 'months' && (
            <div className="grid grid-cols-4 gap-1 px-3 py-3">
              {MONTH_LABELS.map((label, idx) => {
                const isSelectedMonth =
                  selected
                    ? getYear(selected) === currentYear && getMonth(selected) === idx
                    : false;
                const isTodayMonth = nowYear === currentYear && nowMonth === idx;

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => selectMonth(idx)}
                    className={cn(
                      CELL_BASE,
                      isSelectedMonth
                        ? CELL_SELECTED
                        : isTodayMonth
                          ? CELL_TODAY
                          : CELL_DEFAULT,
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* View: anos — grade 4×3 de 12 anos */}
          {view === 'years' && (
            <div className="grid grid-cols-4 gap-1 px-3 py-3">
              {yearGroup.map((yr) => {
                const isSelectedYear = selected ? getYear(selected) === yr : false;
                const isTodayYear = nowYear === yr;

                return (
                  <button
                    key={yr}
                    type="button"
                    onClick={() => selectYear(yr)}
                    className={cn(
                      CELL_BASE,
                      isSelectedYear
                        ? CELL_SELECTED
                        : isTodayYear
                          ? CELL_TODAY
                          : CELL_DEFAULT,
                    )}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-[var(--popover-border,var(--card-border))] px-3 py-1.5">
            <button
              type="button"
              onClick={clear}
              className="text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={goToday}
              className="text-[11px] text-[var(--accent)] hover:opacity-80 transition"
            >
              Hoje
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
